import { useContext, useState } from 'react'
import styled from '@emotion/styled'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import GetAppIcon from '@mui/icons-material/GetApp'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { FileUploadModal, DialogContentStyled } from '@components'
import { submissionContext, templateContext } from '@contexts'
import { colors } from '@global/constants'
import { api } from 'api'
import { BulkUploadError } from './bulk-upload-error'
import { useDatasetDataContext } from '../../datasets/dataset-details/datasetData/context'
import * as SnackbarActions from '../../global/snackbar/snackbar.actions'
import { downloadDataAsCsv, parseFilenameFromContentDisposition } from '../../global/utils/fileUtil'
import { t } from '@lingui/core/macro'
import PropTypes from 'prop-types'

const SuccessPhase = () => (
  <DialogContentStyled>
    <CheckCircleIcon style={{ color: colors.green.light, fontSize: '50px', margin: '20px' }} />
    <SuccessMessage>
      <Typography variant="h4">{t`Successfully uploaded!`}</Typography>
      <Typography variant="body1" style={{ padding: '20px 64px', textAlign: 'center' }}>
        {t`We've successfully started processing and validating your data. Sit back and relax, we've got this. We'll let you know if something goes wrong.`}
      </Typography>
    </SuccessMessage>
  </DialogContentStyled>
)

export const BulkUploadModal = ({ templateId, requireApproval, setOpen, ...rest }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { importBulkUploadTemplate } = useContext(templateContext)
  const { resetSubmissionByDatasetId } = useContext(submissionContext)
  const { dispatch: dispatchState } = useDatasetDataContext()
  const [datasetId, setDatasetId] = useState(null)

  function downloadTemplateFile() {
    api
      .get(`/api/v1/account/template/${templateId}/export`)
      .then(response => {
        const contentDisposition = response.headers['content-disposition'] || ''
        const fileName = parseFilenameFromContentDisposition(contentDisposition)
        downloadDataAsCsv(response.data, fileName)
      })
      .catch(() => {
        dispatch(
          SnackbarActions.showSnackbar({
            message: t`Download template failed.`,
            type: 'ERROR',
            duration: 8000,
          }),
        )
      })
  }

  async function importItemsAndAdvancePhase(formData) {
    formData.append('template', formData.get('selectedFile'))
    formData.delete('selectedFile')
    const response = await importBulkUploadTemplate(templateId, formData)
    const submissionId = response.data.submissionIds[0]
    const submissionDto = (await api.get(`/api/v1/account/submissions/${submissionId}`)).data
    setDatasetId(submissionDto.datasetId)
    return response
  }

  const updateProgressPath = () => {
    setOpen(false)
    if (datasetId) {
      history.push(`/codes/details/${datasetId}/data/processing`)
      resetSubmissionByDatasetId(datasetId)
      dispatchState({ type: 'editMode', payload: { isEnabled: false } })
      return
    }
    history.push('/codes/submissions')
  }

  return (
    <FileUploadModal
      data-test-file-upload-modal="bulk-upload"
      title={t`Import CSV File`}
      onUpload={importItemsAndAdvancePhase}
      setOpen={setOpen}
      SuccessPhase={SuccessPhase}
      requireApproval={requireApproval}
      renderErrors={errors => errors?.map((error, i) => <BulkUploadError error={error} key={i} />)}
      decorateActions={(defaultActions, { phase }) => {
        switch (phase) {
          case FileUploadModal.PHASES.START:
          case FileUploadModal.PHASES.ERROR:
            return (
              <>
                <Button
                  onClick={downloadTemplateFile}
                  color="secondary"
                  variant="outlined"
                  data-testid="download-template-file-button"
                >
                  <GetAppIcon
                    color="secondary"
                    style={{
                      marginBottom: '2px',
                      marginRight: '10px',
                    }}
                  />
                  {t`Download template file`}
                </Button>
                {defaultActions}
              </>
            )
          case FileUploadModal.PHASES.SUCCESS:
            return (
              <>
                {defaultActions}
                <Button
                  name="view-progress"
                  onClick={updateProgressPath}
                  variant="outlined"
                  color="primary"
                  autoFocus
                  data-testid="view-progress-button"
                >
                  {t`View progress`}
                </Button>
              </>
            )
        }
        return defaultActions
      }}
      {...rest}
    />
  )
}

BulkUploadModal.propTypes = {
  templateId: PropTypes.string.isRequired,
  requireApproval: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
}

// Styled Components

const SuccessMessage = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
`
